import React, { useState, useEffect } from "react";
import { createEvent, updateEvent } from "./adminEventService";
import { fetchActiveSpeakers } from "../services/adminSpeakerServices";

const IST = "Asia/Kolkata";

/** Convert UTC ISO string → "YYYY-MM-DDTHH:mm" in IST for datetime-local input */
const utcToISTInput = (utcStr) => {
  if (!utcStr) return "";
  const d = new Date(utcStr);
  if (isNaN(d.getTime())) return "";
  const ist = new Date(d.toLocaleString("en-US", { timeZone: IST }));
  const pad = (n) => String(n).padStart(2, "0");
  return `${ist.getFullYear()}-${pad(ist.getMonth() + 1)}-${pad(ist.getDate())}T${pad(ist.getHours())}:${pad(ist.getMinutes())}`;
};

/** Convert "YYYY-MM-DDTHH:mm" (IST local input) → ISO 8601 with +05:30 offset */
const istInputToISO = (localStr) => {
  if (!localStr) return null;
  return `${localStr}:00+05:30`;
};

const EMPTY = {
  title: "",
  description: "",
  eventStartTime: "",
  eventDuration: 60,
  category: "Workshop",
  level: "Beginner",
  speakerId: "",
  originalPrice: "",
  price: "",
  language: "English",
  platform: "Zoom",
  maxParticipants: "",
  highlights: "",
  topBannerText: "",
  totalAttendees: "",
  bonusValue: "",
  bonusDeadline: "",
};

const AdminEventForm = ({ event, onSaved, onCancel }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [speakers, setSpeakers] = useState([]);

  useEffect(() => {
    fetchActiveSpeakers()
      .then(res => setSpeakers(res.data?.data || res.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (event) {
      setForm({
        title: event.title || "",
        description: event.description || "",
        eventStartTime: utcToISTInput(event.eventStartTime),
        eventDuration: event.eventDuration ?? 60,
        category: event.category || "Workshop",
        level: event.level || "Beginner",
        speakerId: event.speakerId ?? "",
        originalPrice: event.originalPrice ?? "",
        price: event.price ?? "",
        language: event.language || "English",
        platform: event.platform || "Zoom",
        maxParticipants: event.maxParticipants ?? "",
        highlights: Array.isArray(event.highlights) ? event.highlights.join(", ") : (event.highlights || ""),
        topBannerText: event.topBannerText || "",
        totalAttendees: event.totalAttendees ?? "",
        bonusValue: event.bonusValue ?? "",
        bonusDeadline: utcToISTInput(event.bonusDeadline),
      });
    } else {
      setForm(EMPTY);
    }
  }, [event]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const buildPayload = () => {
    const highlights = form.highlights
      ? form.highlights.split(",").map((h) => h.trim()).filter(Boolean)
      : [];

    return {
      title: form.title,
      description: form.description,
      eventStartTime: istInputToISO(form.eventStartTime),
      eventDuration: Number(form.eventDuration),
      category: form.category,
      level: form.level,
      speakerId: form.speakerId !== "" ? Number(form.speakerId) : null,
      originalPrice: form.originalPrice !== "" ? Number(form.originalPrice) : null,
      price: form.price !== "" ? Number(form.price) : null,
      language: form.language,
      platform: form.platform,
      maxParticipants: form.maxParticipants !== "" ? Number(form.maxParticipants) : null,
      highlights,
      topBannerText: form.topBannerText.trim() || null,
      totalAttendees: form.totalAttendees !== "" ? Number(form.totalAttendees) : null,
      bonusValue: form.bonusValue !== "" ? Number(form.bonusValue) : null,
      bonusDeadline: istInputToISO(form.bonusDeadline),
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.title.trim()) { setError("Title is required"); return; }
    if (!form.eventStartTime) { setError("Event start time is required"); return; }
    if (!form.speakerId) { setError("Speaker ID is required"); return; }

    setSaving(true);
    const payload = buildPayload();
    const res = event
      ? await updateEvent(event.id, payload)
      : await createEvent(payload);
    setSaving(false);

    if (res.status === 200 || res.status === 201) {
      onSaved();
    } else {
      setError(res.message || "Failed to save event");
    }
  };

  return (
    <div className="form-section enhanced-admin-form" style={{ marginBottom: 32 }}>
      <div className="section-title">
        {event ? "✏️ Edit Event" : "➕ Create Event"}
      </div>

      {error && (
        <div style={{ background: "#fecaca", color: "#991b1b", padding: "12px 16px", borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>

        {/* Basic Info */}
        <div className="form-row">
          <div className="form-group full-width">
            <label>Title *</label>
            <input value={form.title} onChange={set("title")} placeholder="e.g. Speak Bold Masterclass" required />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group full-width">
            <label>Description</label>
            <textarea rows={3} value={form.description} onChange={set("description")} placeholder="Short description of the event" />
          </div>
        </div>

        {/* Schedule */}
        <div className="form-row">
          <div className="form-group">
            <label>Event Start Time (IST) *</label>
            <input type="datetime-local" value={form.eventStartTime} onChange={set("eventStartTime")} required />
            <span className="field-help">Displayed & stored in IST (+05:30)</span>
          </div>
          <div className="form-group">
            <label>Duration (minutes)</label>
            <input type="number" min={1} value={form.eventDuration} onChange={set("eventDuration")} />
          </div>
        </div>

        {/* Category / Level / Speaker */}
        <div className="form-row">
          <div className="form-group">
            <label>Category</label>
            <select value={form.category} onChange={set("category")}>
              {["Workshop", "Masterclass", "Webinar", "Bootcamp", "Session"].map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Level</label>
            <select value={form.level} onChange={set("level")}>
              {["Beginner", "Intermediate", "Advanced", "All Levels"].map((l) => (
                <option key={l}>{l}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Speaker *</label>
            <select value={form.speakerId} onChange={set("speakerId")} required>
              <option value="">-- Select Speaker --</option>
              {speakers.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Language</label>
            <input value={form.language} onChange={set("language")} placeholder="English" />
          </div>
        </div>

        {/* Pricing */}
        <div className="form-row">
          <div className="form-group">
            <label>Original Price (₹)</label>
            <input type="number" min={0} value={form.originalPrice} onChange={set("originalPrice")} placeholder="e.g. 2999" />
          </div>
          <div className="form-group">
            <label>Sale Price (₹)</label>
            <input type="number" min={0} value={form.price} onChange={set("price")} placeholder="e.g. 149" />
          </div>
        </div>

        {/* Capacity / Platform */}
        <div className="form-row">
          <div className="form-group">
            <label>Max Participants</label>
            <input type="number" min={1} value={form.maxParticipants} onChange={set("maxParticipants")} placeholder="e.g. 500" />
          </div>
          <div className="form-group">
            <label>Platform</label>
            <select value={form.platform} onChange={set("platform")}>
              {["Zoom", "Google Meet", "Teams", "Other"].map((p) => (
                <option key={p}>{p}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Highlights */}
        <div className="form-row">
          <div className="form-group full-width">
            <label>Highlights</label>
            <input value={form.highlights} onChange={set("highlights")} placeholder="Live Q&A, Certificate, Recording access (comma-separated)" />
            <span className="field-help">Comma-separated list</span>
          </div>
        </div>

        {/* ── NEW FIELDS ── */}
        <div className="section-title" style={{ marginTop: 24, fontSize: 16 }}>
          🚀 Booking Page Enhancements
        </div>

        <div className="form-row">
          <div className="form-group full-width">
            <label>Top Banner Text</label>
            <input value={form.topBannerText} onChange={set("topBannerText")} placeholder="e.g. ⚡ 95% OFF — Only for next 24 hours (leave blank to hide)" />
            <span className="field-help">Shown as a sticky banner on the booking page. Leave blank to hide.</span>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Total Attendees (social proof)</label>
            <input type="number" min={0} value={form.totalAttendees} onChange={set("totalAttendees")} placeholder="e.g. 249497" />
            <span className="field-help">Shown as "Join X+ professionals". Leave blank to hide.</span>
          </div>
          <div className="form-group">
            <label>Bonus Value (₹)</label>
            <input type="number" min={0} value={form.bonusValue} onChange={set("bonusValue")} placeholder="e.g. 16000" />
            <span className="field-help">Shown as "₹X Worth of Bonuses". Leave blank to hide.</span>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Bonus Deadline (IST)</label>
            <input type="datetime-local" value={form.bonusDeadline} onChange={set("bonusDeadline")} />
            <span className="field-help">"Register Before [date] to Unlock Bonuses". Leave blank to hide.</span>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
          <button type="submit" className="enhanced-submit" disabled={saving}>
            {saving ? "⏳ Saving..." : event ? "💾 Update Event" : "✅ Create Event"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            style={{ padding: "16px 24px", border: "2px solid #e5e7eb", borderRadius: 8, background: "white", cursor: "pointer", fontWeight: 600 }}
          >
            Cancel
          </button>
        </div>

      </form>
    </div>
  );
};

export default AdminEventForm;
